import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { Source } from '../../models/chat.model';
import { S3FileService } from '../../../../../../../../../goldstar-share/src/app/services/s3file-service';

export interface GroupedSource {
	title: string;
	s3FileVersionGUID: string;
	provenance: string;
	icon: string;
	excerpts: string[];
	sources: Source[];
}

@Component({
	selector: 'app-source-citation',
	templateUrl: './source-citation.component.html',
	styleUrls: ['./source-citation.component.scss'],
})
export class SourceCitationComponent implements OnChanges {
	@Input() sources: Source[] = [];
	@Input() mode: 'rag' | 'direct' = 'rag';
	@Input() isSidecar = false;
	@Input() userQuery = '';
	@Output() viewDocument = new EventEmitter<{ title: string; s3FileVersionGUID: string }>();
	@Output() requestFullscreen = new EventEmitter<{ title: string; s3FileVersionGUID: string }>();

	expanded = true;
	expandedGroups = new Set<string>();
	loadingDocUri: string | null = null;

	/** Cache of resolved filenames keyed by s3FileVersionGUID */
	private resolvedNames = new Map<string, string>();

	constructor(private s3FileService: S3FileService) {}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['sources']) {
			this.resolveFileNames();
		}
	}

	/** Fetch filenames for all unique s3FileVersionGUIDs that haven't been resolved yet */
	private resolveFileNames(): void {
		const guids = [...new Set(this.sources.map((s) => s.s3FileVersionGUID).filter(Boolean))];
		for (const guid of guids) {
			if (this.resolvedNames.has(guid)) continue;
			this.s3FileService.fetchS3FilePreSignedUrl({ s3FileVersionGUID: guid, createDownloadLink: false }).then((result) => {
				if (result?.fileName) {
					this.resolvedNames.set(guid, result.fileName);
				}
			});
		}
	}

	toggleExpanded(): void {
		this.expanded = !this.expanded;
	}

	/** Group sources by document (same s3FileVersionGUID = same document) */
	get groupedSources(): GroupedSource[] {
		const groups = new Map<string, GroupedSource>();

		for (const source of this.sources) {
			const key = source.s3FileVersionGUID;
			if (!key) continue;
			if (!groups.has(key)) {
				groups.set(key, {
					title: this.resolvedNames.get(key) ?? 'Loading…',
					s3FileVersionGUID: key,
					provenance: 'INTERNAL KB',
					icon: 'description',
					excerpts: [],
					sources: [],
				});
			}
			const group = groups.get(key)!;
			group.sources.push(source);
			const sections = this.splitExcerptToQuotes(source.excerpt);
			group.excerpts.push(...sections);
		}

		return Array.from(groups.values());
	}

	/** Split an excerpt into individual quotable passages, prioritizing query-relevant sections */
	private splitExcerptToQuotes(excerpt: string): string[] {
		if (!excerpt) return [];

		// Split on paragraph boundaries
		const paragraphs = excerpt
			.split(/\n\n+/)
			.map((p) => p.trim())
			.filter((p) => p.length > 30);
		const queryTerms = this.userQuery ? this.extractQueryTerms(this.userQuery) : [];

		// If we have multiple paragraphs, score and prioritize by relevance
		if (paragraphs.length > 1 && queryTerms.length > 0) {
			const scored = paragraphs.map((p) => ({
				text: p,
				score: this.scoreRelevance(p, queryTerms),
			}));
			scored.sort((a, b) => b.score - a.score);
			const relevant = scored.filter((s) => s.score > 0).map((s) => s.text);
			if (relevant.length > 0) return relevant;
			return paragraphs;
		}

		if (paragraphs.length > 1) return paragraphs;

		// Single block of text — find the relevant window based on query terms
		const text = paragraphs.length > 0 ? paragraphs[0] : excerpt.trim();
		if (queryTerms.length > 0) {
			const relevant = this.findRelevantWindow(text, queryTerms);
			if (relevant) return [relevant];
		}

		// No query or no match — show from the start but try to find a meaningful sentence
		return [text];
	}

	/** Extract meaningful search terms from the user's query */
	private extractQueryTerms(query: string): string[] {
		const stopWords = new Set(['tell', 'me', 'more', 'about', 'what', 'is', 'the', 'a', 'an', 'can', 'you', 'how', 'does', 'do', 'are', 'in', 'of', 'for', 'to', 'and', 'or', 'with']);
		return query
			.toLowerCase()
			.split(/\s+/)
			.filter((w) => w.length > 2 && !stopWords.has(w));
	}

	/** Score a paragraph's relevance to the query terms */
	private scoreRelevance(text: string, queryTerms: string[]): number {
		const lower = text.toLowerCase();
		let score = 0;
		for (const term of queryTerms) {
			if (lower.includes(term)) score += 1;
			// Bonus for term appearing near the start (likely a header/topic sentence)
			if (lower.indexOf(term) < 50) score += 0.5;
		}
		return score;
	}

	/** Find a relevant window of text within a single block based on query terms */
	private findRelevantWindow(text: string, queryTerms: string[]): string | null {
		const lower = text.toLowerCase();

		// Find the best position — prefer the first occurrence of the most specific term
		let bestPos = -1;
		let bestTermLen = 0;
		for (const term of queryTerms) {
			const pos = lower.indexOf(term);
			if (pos >= 0 && (bestPos < 0 || term.length > bestTermLen)) {
				bestPos = pos;
				bestTermLen = term.length;
			}
		}
		if (bestPos < 0) return null;

		// Find the sentence that contains the match
		// Look backwards for sentence start
		let start = bestPos;
		const sentenceStartSearch = text.lastIndexOf('. ', bestPos);
		if (sentenceStartSearch >= 0 && bestPos - sentenceStartSearch < 200) {
			start = sentenceStartSearch + 2;
		} else {
			// Try to find a capital letter that starts a sentence
			for (let i = bestPos; i >= Math.max(0, bestPos - 150); i--) {
				if (i === 0 || (text[i - 1] === ' ' && text[i] >= 'A' && text[i] <= 'Z')) {
					start = i;
					break;
				}
			}
		}

		// Extract ~400 chars from the start point, trim at sentence end
		const rawEnd = Math.min(text.length, start + 400);
		const window = text.substring(start, rawEnd);

		// Trim to last complete sentence
		const lastPeriod = Math.max(window.lastIndexOf('. '), window.lastIndexOf('.\n'), window.lastIndexOf('.'));
		if (lastPeriod > 100) {
			return window.substring(0, lastPeriod + 1).trim();
		}

		return window.trim();
	}

	/** Get visible excerpts (truncated or full) */
	getVisibleExcerpts(group: GroupedSource): string[] {
		if (this.expandedGroups.has(group.s3FileVersionGUID)) {
			return group.excerpts;
		}
		// Show max 3 excerpts when collapsed
		return group.excerpts.slice(0, 3);
	}

	hasMoreExcerpts(group: GroupedSource): boolean {
		return group.excerpts.length > 3 && !this.expandedGroups.has(group.s3FileVersionGUID);
	}

	toggleGroup(key: string): void {
		if (this.expandedGroups.has(key)) {
			this.expandedGroups.delete(key);
		} else {
			this.expandedGroups.add(key);
		}
	}

	/** Truncate a single quote to ~250 chars at a sentence boundary */
	truncateQuote(text: string): string {
		if (text.length <= 250) return text;
		// Find sentence end near 250 chars
		const searchWindow = text.substring(0, 300);
		const lastSentence = searchWindow.match(/.*[.!?]/s);
		if (lastSentence && lastSentence[0].length >= 80) {
			return lastSentence[0];
		}
		const cut = text.lastIndexOf(' ', 250);
		return text.substring(0, cut > 80 ? cut : 250) + '…';
	}

	trackByGuid(_index: number, group: GroupedSource): string {
		return group.s3FileVersionGUID;
	}

	onViewDocument(group: GroupedSource): void {
		const payload = { title: group.title, s3FileVersionGUID: group.s3FileVersionGUID };
		this.loadingDocUri = group.s3FileVersionGUID;
		this.viewDocument.emit(payload);
		setTimeout(() => (this.loadingDocUri = null), 2000);
	}
}
